import { memo, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, Outlet, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { resolveRole, getMentorPLG, isManagerOrAbove } from "@/lib/roles";
import { ROUTES } from "@/lib/routes";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { lazyWithReload } from "@/lib/lazyWithReload";

const Login = lazyWithReload(() => import("@/pages/Login"));
const ExecutiveSummary = lazyWithReload(() => import("@/pages/ExecutiveSummary"));
const MentorOperations = lazyWithReload(() => import("@/pages/MentorOperations"));
const LearnerRisk = lazyWithReload(() => import("@/pages/LearnerRisk"));
const PLGDirectory = lazyWithReload(() => import("@/pages/PLGDirectory"));
const PLGDetailPage = lazyWithReload(() => import("@/pages/PLGDetail"));
const LearnerSearch = lazyWithReload(() => import("@/pages/LearnerSearch"));
const MentorSelect = lazyWithReload(() => import("@/pages/MentorSelect"));
const MentorView = lazyWithReload(() => import("@/pages/MentorView"));
const LearnerProfile = lazyWithReload(() => import("@/pages/LearnerProfile"));
const DataQuality = lazyWithReload(() => import("@/pages/DataQuality"));
const MentorDirectory = lazyWithReload(() => import("@/pages/MentorDirectory"));
const Import = lazyWithReload(() => import("@/pages/Import"));
const FeedbackInbox = lazyWithReload(() => import("@/pages/FeedbackInbox"));
const ReportProgress = lazyWithReload(() => import("@/pages/ReportProgress"));
const BookMentor = lazyWithReload(() => import("@/pages/BookMentor"));
const BookingConfirmation = lazyWithReload(() => import("@/pages/BookingConfirmation"));
const PrivacyPolicy = lazyWithReload(() => import("@/pages/PrivacyPolicy"));
const LearningAnalytics = lazyWithReload(() => import("@/pages/LearningAnalytics"));
const CohortAnalytics = lazyWithReload(() => import("@/pages/CohortAnalytics"));
const ScheduledPosts = lazyWithReload(() => import("@/pages/ScheduledPosts"));
const Settings = lazyWithReload(() => import("@/pages/Settings"));
const NotFound = lazyWithReload(() => import("@/pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const PageLoader = (
  <div className="min-h-screen flex items-center justify-center">
    <div className="flex flex-col items-center gap-2">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
);

function ProtectedRoute({ children, adminOnly }: { children?: React.ReactNode; adminOnly?: boolean }) {
  const { user, role: claimsRole, memberships, loading } = useAuth();
  
  if (loading && !user) return PageLoader;
  if (!user) return <Navigate to={ROUTES.LOGIN} replace />;
  
  const role = resolveRole(claimsRole, user.email);
  if (adminOnly && !isManagerOrAbove(role)) {
    if (role === "mentor") {
      const plg = getMentorPLG(memberships);
      if (plg) return <Navigate to={ROUTES.MENTOR_VIEW(plg)} replace />;
      return <Navigate to={ROUTES.MENTOR_SELECT} replace />;
    }
    // learner / unknown: no client-side route matches their role, send them
    // to the same fallback RootRedirect uses for non-manager, non-mentor users.
    return <Navigate to={ROUTES.MENTOR_SELECT} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

function RootRedirect() {
  const { user, role: claimsRole, memberships } = useAuth();
  if (!user) return <Navigate to={ROUTES.LOGIN} replace />;
  const role = resolveRole(claimsRole, user.email);
  if (role === "mentor") {
    const plg = getMentorPLG(memberships);
    if (plg) return <Suspense fallback={PageLoader}><MentorView plgNameOverride={plg} /></Suspense>;
    return <Suspense fallback={PageLoader}><MentorSelect /></Suspense>;
  }
  if (isManagerOrAbove(role)) {
    return <Suspense fallback={PageLoader}><ExecutiveSummary /></Suspense>;
  }
  return <Suspense fallback={PageLoader}><MentorSelect /></Suspense>;
}

function MentorSelectRedirect() {
  const { role: claimsRole, memberships, user } = useAuth();
  const role = resolveRole(claimsRole, user?.email);
  if (role === "mentor") {
    const plg = getMentorPLG(memberships);
    if (plg) return <Suspense fallback={PageLoader}><MentorView plgNameOverride={plg} /></Suspense>;
  }
  return <Suspense fallback={PageLoader}><MentorSelect /></Suspense>;
}

function MentorScopeGuard({ children }: { children: React.ReactNode }) {
  const { plgName } = useParams<{ plgName: string }>();
  const { user, role: claimsRole, memberships } = useAuth();
  const role = resolveRole(claimsRole, user?.email);
  if (role === "mentor") {
    const own = getMentorPLG(memberships);
    if (!own) return <Navigate to={ROUTES.MENTOR_SELECT} replace />;
    if (plgName !== own) return <Navigate to={ROUTES.MENTOR_VIEW(own)} replace />;
  }
  return <>{children}</>;
}

const MemoizedDashboardLayout = memo(DashboardLayout);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path={ROUTES.LOGIN} element={<Suspense fallback={PageLoader}><Login /></Suspense>} />
            {/* Public, unauthenticated — 10k+ learners self-report without logging in */}
            <Route path={ROUTES.REPORT_PROGRESS} element={<Suspense fallback={PageLoader}><ReportProgress /></Suspense>} />
            <Route path="/book/confirm" element={<Suspense fallback={PageLoader}><BookingConfirmation /></Suspense>} />
            <Route path="/book/:mentorEmail" element={<Suspense fallback={PageLoader}><BookMentor /></Suspense>} />
            <Route path={ROUTES.PRIVACY_POLICY} element={<Suspense fallback={PageLoader}><PrivacyPolicy /></Suspense>} />
            
            {/* Authenticated Routes with shared DashboardLayout */}
            <Route element={<ProtectedRoute><MemoizedDashboardLayout /></ProtectedRoute>}>
              <Route index element={<Suspense fallback={PageLoader}><RootRedirect /></Suspense>} />
              
              {/* Program Health merged into Executive Summary; redirect old links/bookmarks */}
              <Route path={ROUTES.HEALTH} element={<Navigate to={ROUTES.HOME} replace />} />
              <Route path={ROUTES.MENTORS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><MentorOperations /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.RISK} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><LearnerRisk /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.PLGS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><PLGDirectory /></Suspense></ProtectedRoute>} />
              <Route path="/plgs/:id" element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><PLGDetailPage /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.LEARNERS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><LearnerSearch /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.MENTOR_DIRECTORY} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><MentorDirectory /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.MENTOR_SELECT} element={<MentorSelectRedirect />} />
              <Route path="/mentor/:plgName" element={<MentorScopeGuard><Suspense fallback={PageLoader}><MentorView /></Suspense></MentorScopeGuard>} />
              <Route path="/mentor/:plgName/learner/:learnerId" element={<MentorScopeGuard><Suspense fallback={PageLoader}><LearnerProfile /></Suspense></MentorScopeGuard>} />
              <Route path={ROUTES.DATA_QUALITY} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><DataQuality /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.IMPORT} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><Import /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.FEEDBACK} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><FeedbackInbox /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.LEARNING_ANALYTICS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><LearningAnalytics /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.COHORT_ANALYTICS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><CohortAnalytics /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.SCHEDULED_POSTS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><ScheduledPosts /></Suspense></ProtectedRoute>} />
              <Route path={ROUTES.SETTINGS} element={<ProtectedRoute adminOnly><Suspense fallback={PageLoader}><Settings /></Suspense></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
